import { CheckCircle } from "lucide-react"
import type { Benefit } from "../types"

interface BenefitsSectionProps {
  /** Benefits to display */
  benefits: Benefit[]
}

/**
 * Benefits section showing key advantages of the use case.
 * Displays metrics and descriptions in a grid layout.
 */
export function BenefitsSection({ benefits }: BenefitsSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-2xl font-bold text-neutral-900 sm:text-3xl">
            Key Benefits
          </h2>
          <p className="mx-auto max-w-2xl text-neutral-600">
            Measurable results you can expect when deploying this AI solution.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon || CheckCircle
            return (
              <div
                key={index}
                className="group relative rounded-2xl border border-neutral-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary/20"
              >
                {/* Icon with optional metric */}
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 ring-1 ring-primary/10">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  {benefit.metric && (
                    <span className="text-2xl font-bold text-primary">
                      {benefit.metric}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                  {benefit.title}
                </h3>

                {/* Description */}
                <p className="text-sm leading-relaxed text-neutral-600">
                  {benefit.description}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
